<template>
  <div class="ai-hologram-brain">
    <div class="brain-waves brain-waves--left"></div>
    <div class="brain-waves brain-waves--right"></div>
    <div class="brain-orbit orbit-one"></div>
    <div class="brain-orbit orbit-two"></div>
    <div class="brain-platform"></div>
    <div class="brain-platform-ring"></div>
    <div class="brain-beam"></div>
    <div class="brain-core">
      <svg viewBox="0 0 200 150" class="brain-svg" aria-hidden="true">
        <defs>
          <linearGradient
            id="brainLineGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="26%" stop-color="#d6e8ff" />
            <stop offset="62%" stop-color="#7ea6ff" />
            <stop offset="100%" stop-color="#72d8ff" />
          </linearGradient>
        </defs>
        <path
          d="M98 17c-15 0-28 7-35 19-15 1-27 14-27 29 0 8 3 15 8 21-2 4-3 8-3 13 0 19 15 34 35 34h16c4 7 11 11 19 11s15-4 19-11h12c20 0 35-15 35-34 0-5-1-9-3-13 5-6 8-13 8-21 0-15-12-28-27-29-7-12-20-19-35-19-8 0-16 2-22 7-6-5-14-7-22-7Z"
          fill="rgba(255,255,255,0.08)"
          stroke="url(#brainLineGradient)"
          stroke-width="3"
          stroke-linejoin="round"
        />
        <path
          d="M71 43c7 8 8 18 2 28m56-28c-7 8-8 18-2 28M70 89c10 4 20 5 30 1m30-1c10 4 20 3 30-1M84 60c7 7 7 17 0 24m33-24c-7 7-7 17 0 24"
          fill="none"
          stroke="rgba(255,255,255,0.56)"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
      <span class="brain-ai">AI</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'AiHologramBrain',
});
</script>

<style scoped>
.ai-hologram-brain {
  position: relative;
  height: clamp(240px, 32vw, 370px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: brainFloat 5s ease-in-out infinite;
}

.brain-core {
  position: relative;
  z-index: 3;
  width: clamp(220px, 28vw, 340px);
  aspect-ratio: 1.25;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.54),
    rgba(207, 227, 255, 0.1) 62%,
    transparent 78%
  );
  filter: drop-shadow(0 0 28px rgba(114, 216, 255, 0.36));
}

.brain-svg {
  width: 100%;
  height: 100%;
  animation: brainPulse 3.8s ease-in-out infinite;
}

.brain-ai {
  position: absolute;
  font-size: clamp(50px, 7vw, 88px);
  font-weight: 700;
  letter-spacing: -0.08em;
  color: rgba(255, 255, 255, 0.92);
  text-shadow:
    0 0 16px rgba(114, 216, 255, 0.55),
    0 0 30px rgba(91, 140, 255, 0.34);
}

.brain-orbit {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(179, 216, 255, 0.82);
  filter: drop-shadow(0 0 12px rgba(114, 216, 255, 0.32));
}

.orbit-one {
  width: clamp(240px, 32vw, 410px);
  height: clamp(70px, 8vw, 112px);
  transform: rotate(10deg);
  animation: orbitDrift 7s linear infinite;
}

.orbit-two {
  width: clamp(250px, 33vw, 430px);
  height: clamp(90px, 10vw, 136px);
  transform: rotate(-16deg);
  animation: orbitDriftReverse 8s linear infinite;
}

.brain-platform {
  position: absolute;
  bottom: 30px;
  width: clamp(220px, 32vw, 380px);
  height: clamp(72px, 8vw, 108px);
  border-radius: 50%;
  background:
    radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.38),
      rgba(146, 182, 255, 0.08) 48%,
      transparent 64%
    ),
    radial-gradient(
      circle at 50% 50%,
      rgba(114, 216, 255, 0.34),
      transparent 72%
    );
  border: 1px solid rgba(188, 219, 255, 0.62);
  box-shadow:
    inset 0 0 24px rgba(255, 255, 255, 0.58),
    0 0 30px rgba(114, 216, 255, 0.24);
}

.brain-platform-ring {
  position: absolute;
  bottom: 8px;
  width: clamp(280px, 40vw, 520px);
  height: clamp(100px, 12vw, 150px);
  border-radius: 50%;
  border: 1px solid rgba(179, 212, 255, 0.28);
  box-shadow:
    0 0 0 12px rgba(215, 235, 255, 0.08),
    0 0 0 26px rgba(182, 216, 255, 0.05);
}

.brain-beam {
  position: absolute;
  bottom: 62px;
  width: 180px;
  height: 120px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5),
    rgba(114, 216, 255, 0.06)
  );
  clip-path: polygon(42% 0, 58% 0, 100% 100%, 0 100%);
  filter: blur(14px);
  opacity: 0.58;
  animation: beamFlicker 2.6s ease-in-out infinite;
}

.brain-waves {
  position: absolute;
  top: 46%;
  width: 92px;
  height: 52px;
  transform: translateY(-50%);
}

.brain-waves::before,
.brain-waves::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 3px solid rgba(192, 223, 255, 0.82);
  border-left-color: transparent;
  border-right-color: transparent;
  animation: waveEcho 2.8s ease-in-out infinite;
}

.brain-waves::after {
  inset: 10px 14px;
  opacity: 0.7;
  animation-delay: 0.24s;
}

.brain-waves--left {
  left: 10%;
}

.brain-waves--right {
  right: 10%;
}

@keyframes brainFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes brainPulse {
  0%,
  100% {
    opacity: 0.86;
    transform: scale(0.985);
  }
  50% {
    opacity: 1;
    transform: scale(1.03);
  }
}

@keyframes orbitDrift {
  from {
    transform: rotate(10deg);
  }
  to {
    transform: rotate(370deg);
  }
}

@keyframes orbitDriftReverse {
  from {
    transform: rotate(-16deg);
  }
  to {
    transform: rotate(-376deg);
  }
}

@keyframes waveEcho {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes beamFlicker {
  0%,
  100% {
    opacity: 0.42;
  }
  50% {
    opacity: 0.78;
  }
}

@media (max-width: 768px) {
  .ai-hologram-brain {
    height: 200px;
  }

  .brain-waves {
    display: none;
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .ai-hologram-brain {
    height: 118px;
  }

  .brain-core {
    width: clamp(118px, 34vw, 166px);
  }

  .brain-ai {
    font-size: clamp(34px, 9vw, 50px);
  }

  .orbit-one {
    width: clamp(150px, 42vw, 220px);
    height: clamp(42px, 12vw, 68px);
  }

  .orbit-two {
    width: clamp(164px, 46vw, 238px);
    height: clamp(54px, 14vw, 82px);
  }

  .brain-platform {
    bottom: 14px;
    width: clamp(138px, 40vw, 210px);
    height: clamp(40px, 11vw, 62px);
  }

  .brain-platform-ring {
    bottom: 0;
    width: clamp(186px, 54vw, 286px);
    height: clamp(56px, 16vw, 86px);
    box-shadow:
      0 0 0 8px rgba(215, 235, 255, 0.08),
      0 0 0 16px rgba(182, 216, 255, 0.05);
  }

  .brain-beam {
    bottom: 24px;
    width: 82px;
    height: 54px;
  }
}

@media (orientation: landscape) and (max-height: 820px) {
  .ai-hologram-brain {
    height: 150px;
  }

  .brain-core {
    width: clamp(150px, 18vw, 230px);
  }

  .orbit-one {
    width: clamp(180px, 24vw, 300px);
    height: clamp(52px, 6vw, 82px);
  }

  .orbit-two {
    width: clamp(190px, 25vw, 320px);
    height: clamp(66px, 7vw, 98px);
  }

  .brain-platform {
    bottom: 18px;
    width: clamp(180px, 24vw, 280px);
    height: clamp(50px, 5vw, 80px);
  }

  .brain-platform-ring {
    bottom: 0;
    width: clamp(220px, 30vw, 360px);
    height: clamp(72px, 7vw, 110px);
  }

  .brain-beam {
    bottom: 34px;
    width: 120px;
    height: 78px;
  }

  .brain-waves {
    width: 60px;
    height: 36px;
  }
}
</style>
